import React from 'react';

import { useGetRenderProofQuery } from './api/renderProofApi';
import { RenderProofResponse } from './api/RenderProofResponse';

/**
 * Verification queues the first homepage render (the domain-integration job
 * writes the `Caching...` row), so a proof requested right after the wizard is
 * usually `pending` for a few seconds. Polling lets the screen upgrade itself
 * when that render lands instead of the user having to come back later.
 */
export const RENDER_PROOF_POLL_INTERVAL_MS = 3_000;

/**
 * One minute, then give up: the journey must not wait on a render. Measured
 * integration-to-render waits were 14s, 60s and 2m41s, so a slow render will
 * miss this window on purpose — the customer carries on through the wizard and
 * the dashboard entry point picks the proof up later.
 */
export const RENDER_PROOF_POLL_TIMEOUT_MS = 60_000;

const NO_POLLING = 0;

export type UseRenderProofResult = {
  /**
   * The proof that may be shown. An `origin_error` is withheld until the
   * polling window has run out: it is one sample of a live origin, and a
   * deploy-window 404 on the first poll must not end the wait, flash a warning,
   * and mark the account served while the real render is thirty seconds away.
   * It becomes the answer only once the window closes with nothing better.
   */
  proof: RenderProofResponse | undefined;
  isFetching: boolean;
  isError: boolean;
  /** The homepage render is still on its way and we are still watching for it. */
  isStillRendering: boolean;
};

export function useRenderProof(options: {
  domain: string | undefined;
  skip?: boolean;
}): UseRenderProofResult {
  const { domain, skip } = options;
  const [pollingInterval, setPollingInterval] = React.useState(NO_POLLING);
  const [hasTimedOut, setHasTimedOut] = React.useState(false);

  // `currentData` is scoped to the current argument; `data` keeps serving the
  // previous domain's proof until the new request resolves, which would show one
  // site's numbers under another site's URL.
  const { currentData, isFetching, isError } = useGetRenderProofQuery(
    { domain: domain ?? '' },
    { skip: skip === true || !domain, pollingInterval },
  );

  // `origin_error` keeps the wait alive rather than ending it: the render may
  // still land (a transient 404 heals; the origin may only be refusing *our*
  // fetcher), and a landed render flips the status to `complete` on the next
  // poll, which is a strictly better answer.
  const isAwaitingRender =
    currentData?.status === 'pending' || currentData?.status === 'origin_error';

  // Keyed on the query arguments as well as the awaiting flag: a timer armed for
  // one domain must not stop polling for the next one, and a new domain gets a
  // fresh minute rather than inheriting the previous one's elapsed time.
  React.useEffect(() => {
    if (!isAwaitingRender) {
      setPollingInterval(NO_POLLING);
      setHasTimedOut(false);
      return undefined;
    }

    setPollingInterval(RENDER_PROOF_POLL_INTERVAL_MS);
    setHasTimedOut(false);

    const timer = setTimeout(() => {
      setPollingInterval(NO_POLLING);
      setHasTimedOut(true);
    }, RENDER_PROOF_POLL_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isAwaitingRender, domain, skip]);

  const isStillRendering = isAwaitingRender && !hasTimedOut;

  // An unsettled origin_error is a provisional sample, not an answer.
  const proof =
    currentData?.status === 'origin_error' && isStillRendering
      ? undefined
      : currentData;

  return {
    proof,
    isFetching,
    isError,
    isStillRendering,
  };
}
